﻿import { FunctionComponent, useEffect, useMemo } from "react";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Icon, SimpleGrid, Spinner, Text } from "@chakra-ui/react";
import { useAdminStatClientCount, useAdminStatUserCount, useTenants } from "../../api";
import { useUsers } from "../../api/users";
import { SetPageTitle } from "../../components/SetPageTitle";
import { IconBuildingSkyscraper, IconCircleOff, IconUserCheck, IconUsers } from "@tabler/icons-react";

interface KpiCardProps {
    label: string;
    value: number | undefined;
    isLoading: boolean;
    icon: JSX.Element;
    accentColor: string;
    helpText?: string;
    onClick?: () => void;
}

const KpiCard: FunctionComponent<KpiCardProps> = ({ label, value, isLoading, icon, accentColor, helpText, onClick }) => (
    <Box
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        overflow="hidden"
        cursor={onClick ? "pointer" : undefined}
        _hover={onClick ? { borderColor: `${accentColor}.300`, shadow: "sm" } : undefined}
        transition="all 0.15s"
        onClick={onClick}
        role={onClick ? "button" : undefined}
    >
        <Box h="3px" bg={`${accentColor}.400`} />
        <Flex p={5} align="flex-start" gap={4}>
            <Box flex="1">
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1}>
                    {label}
                </Text>
                {isLoading ? (
                    <Spinner size="sm" color="gray.400" mt={1} />
                ) : (
                    <Text fontSize="2xl" fontWeight="bold" color="gray.800" lineHeight={1}>
                        {value ?? "—"}
                    </Text>
                )}
                {helpText && (
                    <Text fontSize="xs" color="gray.400" mt={1}>
                        {helpText}
                    </Text>
                )}
            </Box>
            <Flex w={10} h={10} borderRadius="lg" bg={`${accentColor}.50`} align="center" justify="center" flexShrink={0}>
                <Icon as={() => icon} boxSize={5} color={`${accentColor}.500`} />
            </Flex>
        </Flex>
    </Box>
);

export const AdminDashboardPage: FunctionComponent = () => {
    const appInsights = useAppInsightsContext();
    const navigate = useNavigate();

    useEffect(() => {
        appInsights.trackPageView({ uri: "/admin" });
    }, []);

    const clientActiveCount = useAdminStatClientCount();
    const userActiveCount = useAdminStatUserCount();
    const usersQuery = useUsers();
    const tenantsQuery = useTenants();

    const selfRegisteredCount = useMemo(() => (usersQuery.data ?? []).filter(u => u.isSelfRegistered).length, [usersQuery.data]);

    const disabledClientCount = useMemo(() => (tenantsQuery.data ?? []).filter(t => !t.isEnabled).length, [tenantsQuery.data]);

    return (
        <Box h="100%" overflow="auto" bg="gray.50">
            <SetPageTitle title="System Settings" />

            {/* Page Header */}
            <Flex align="center" px={6} py={4} bg="white" borderBottom="1px solid" borderColor="gray.200">
                <Text fontSize="xl" fontWeight="semibold">
                    Dashboard
                </Text>
            </Flex>

            <Box px={6} py={6}>
                <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap={4}>
                    <KpiCard
                        label="Active Clients"
                        value={clientActiveCount.data}
                        isLoading={clientActiveCount.isLoading}
                        icon={<Icon as={IconBuildingSkyscraper} />}
                        accentColor="blue"
                        helpText="Enabled tenants"
                        onClick={() => navigate({ pathname: "clients", search: "?status=enabled" })}
                    />
                    <KpiCard
                        label="Active Users"
                        value={userActiveCount.data}
                        isLoading={userActiveCount.isLoading}
                        icon={<Icon as={IconUsers} />}
                        accentColor="teal"
                        helpText="Registered accounts"
                        onClick={() => navigate("users")}
                    />
                    <KpiCard
                        label="Self-Registered"
                        value={usersQuery.isLoading ? undefined : selfRegisteredCount}
                        isLoading={usersQuery.isLoading}
                        icon={<Icon as={IconUserCheck} />}
                        accentColor="orange"
                        helpText="Not AD-provisioned"
                        onClick={() => navigate({ pathname: "users", search: "?registered=self" })}
                    />
                    <KpiCard
                        label="Disabled Clients"
                        value={tenantsQuery.isLoading ? undefined : disabledClientCount}
                        isLoading={tenantsQuery.isLoading}
                        icon={<Icon as={IconCircleOff} />}
                        accentColor="red"
                        helpText="Inactive tenants"
                        onClick={() => navigate({ pathname: "clients", search: "?status=disabled" })}
                    />
                </SimpleGrid>
            </Box>
        </Box>
    );
};
